import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Badge, Col, Container, Row } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { fetchKitchensPage } from "../../services/catalog.api";
import ChefCard from "../home/cards/ChefCard";
import ErrorInfo from "../shared/ErrorInfo";
import LoadMore from "../shared/LoadMore";
import NodataFound from "../shared/NodataFound";
import { motion } from "framer-motion";

export default function AllChefs() {
  const {
    data: pages,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["all-chefs"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchKitchensPage({ page: pageParam, limit: 24 }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
  });

  const data = useMemo(
    () => (pages?.pages || []).flatMap((p) => p.items),
    [pages],
  );
  const total = pages?.pages?.[0]?.pagination?.total ?? data.length;


  if (error) {
    toast.error(error?.message);
    return (
      <ErrorInfo
        message={error?.message}
        details={error?.response?.data?.data}
      />
    );
  }

  if (!isPending && data?.length === 0) {
    return <NodataFound />;
  }

  return (
    <div
      className="container page-navbar-space"
      style={{
        paddingBottom: "2rem",
      }}
    >
      {/* HEADER SECTION */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #facc15 0%, #fde047 40%, #fef9c3 100%)",
          borderRadius: "18px",
          padding: "1.5rem 1.75rem",
          marginBottom: "1.5rem",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: "1.4rem",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Meet Our Featured Chefs
            </h3>

            <p
              style={{
                marginTop: "6px",
                marginBottom: 0,
                fontSize: "0.9rem",
                color: "#374151",
                maxWidth: "520px",
              }}
            >
              Discover home chefs crafting fresh meals with personality,
              passion, and flavor you can trust.
            </p>
          </div>

          <Badge
            style={{
              background: "#111827",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: "999px",
              fontSize: "0.85rem",
              fontWeight: 500,
              boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
            }}
          >
            {isPending ? "Loading..." : `${total} Chefs`}
          </Badge>
        </div>
      </div>

      {/* GRID SECTION */}
      <div
        style={{
          background: "#f9fafb",
          borderRadius: "18px",
          padding: "1.25rem",
        }}
      >
        <Container fluid>
          <Row className="g-3">
            {isPending
              ? Array(8)
                  .fill(0)
                  .map((_, index) => (
                    <Col xl={3} lg={4} md={6} sm={12} key={index}>
                      <div
                        style={{
                          borderRadius: "14px",
                          overflow: "hidden",
                          background: "#fff",
                          padding: "0.75rem",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                        }}
                      >
                        <Skeleton height={220} />
                        <div style={{ marginTop: "10px" }}>
                          <Skeleton height={18} width="70%" />
                          <Skeleton height={14} width="50%" />
                        </div>
                      </div>
                    </Col>
                  ))
              : data?.map((item, index) => (
                  <Col xl={3} lg={4} md={6} sm={12} key={item?._id || index}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.25,
                        delay: index * 0.03,
                      }}
                      whileHover={{ y: -4 }}
                      style={{
                        height: "100%",
                        borderRadius: "14px",
                      }}
                    >
                      <ChefCard chef={item} />
                    </motion.div>
                  </Col>
                ))}
          </Row>
        </Container>

        {!isPending && (
          <LoadMore
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            label="Load more chefs"
          />
        )}
      </div>
    </div>
  );
}
